import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../view-models/api-response';

export interface InitiateLinkingResponse {
	linkingSessionId: number;
	maskedEmail: string;
	expiresAt: string;
	attemptsRemaining: number;
	// Development-only: the plain OTP, returned by the API solely in a Development environment.
	devOtp?: string;
}

export interface ConfirmLinkingResponse {
	success: boolean;
	linkedCustomerId: number;
	attemptsRemaining?: number;
	errorCode?: string;
}

@Injectable({ providedIn: 'root' })
export class Auth0LinkingHttpService {
	private readonly base = 'core-api/Ticketing/Identity/customer/auth0';

	constructor(private http: HttpClient) {}

	public initiate(emailToLink?: string): Observable<ApiResponse<InitiateLinkingResponse>> {
		return this.http.post<ApiResponse<InitiateLinkingResponse>>(
			`${this.base}/initiate-link`,
			{ emailToLink: emailToLink || null }
		);
	}

	public confirm(linkingSessionId: number, otp: string): Observable<ApiResponse<ConfirmLinkingResponse>> {
		return this.http.post<ApiResponse<ConfirmLinkingResponse>>(
			`${this.base}/confirm-link`,
			{ linkingSessionId, otp }
		);
	}

	// Links directly to the customer that owns the Auth0-verified profile email, no OTP. The email is
	// taken server-side from the validated token, so there is nothing to send in the body.
	public linkVerified(): Observable<ApiResponse<ConfirmLinkingResponse>> {
		return this.http.post<ApiResponse<ConfirmLinkingResponse>>(
			`${this.base}/link-verified`,
			{}
		);
	}

	// Creates a brand-new customer for the current (unlinked) Auth0 identity and links it.
	// The email is taken server-side from the Auth0-verified token, never from this payload.
	public createAndLink(payload: any): Observable<ApiResponse<ConfirmLinkingResponse>> {
		return this.http.post<ApiResponse<ConfirmLinkingResponse>>(
			`${this.base}/create-and-link`,
			payload
		);
	}

	// Returns whether a customer with the given email already exists in the current portal,
	// so the UI can decide whether to offer "link this existing account".
	public checkExistingCustomer(email?: string): Observable<ApiResponse<{ exists: boolean }>> {
		return this.http.post<ApiResponse<{ exists: boolean }>>(
			`${this.base}/check-existing-customer`,
			{ email: email || null }
		);
	}
}
